import { Workflow, Plus } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/core/lib/utils';
import { useTranslation } from 'react-i18next';
import type { Task, TaskStatusType } from '../../types';

const STATUS_DOT: Record<TaskStatusType, string> = {
  todo: 'bg-muted-foreground',
  'in-progress': 'bg-blue-500',
  done: 'bg-green-500',
};

interface TaskTitleCellProps {
  task: Task;
  isEditing: boolean;
  expanded: boolean;
  subtaskCount: number;
  disableSubtasks?: boolean;
  depth?: number;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onClick: () => void;
  onDoubleClick: (e: React.MouseEvent) => void;
  onCommitEdit: () => void;
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onToggleExpand: () => void;
  onAddSubtask: () => void;
}

export function TaskTitleCell({
  task,
  isEditing,
  expanded,
  subtaskCount,
  disableSubtasks,
  depth = 0,
  inputRef,
  onClick,
  onDoubleClick,
  onCommitEdit,
  onInputKeyDown,
  onToggleExpand,
  onAddSubtask,
}: TaskTitleCellProps) {
  const { t } = useTranslation('tasks');

  return (
    <div
      className={cn(
        'flex w-56 min-w-0 shrink-0 items-center gap-2 p-3 md:w-80',
        !isEditing && 'cursor-pointer',
      )}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      <span
        className={cn('size-2 shrink-0 rounded-full', STATUS_DOT[task.status.type])}
        aria-hidden
      />

      {isEditing ? (
        <Input
          ref={inputRef}
          data-testid={`tasks-row-${task.id}-title-input`}
          defaultValue={task.title}
          autoFocus
          className="h-6 flex-1 min-w-0 border-border bg-background px-1 py-0 text-sm shadow-none"
          onClick={(e) => e.stopPropagation()}
          onBlur={onCommitEdit}
          onKeyDown={onInputKeyDown}
        />
      ) : (
        <span
          data-testid={`tasks-row-${task.id}-title-text`}
          className={cn(
            'truncate text-sm',
            task.status.type === 'done' && 'line-through text-muted-foreground',
          )}
        >
          {task.title}
        </span>
      )}

      {!isEditing && !disableSubtasks && subtaskCount > 0 && (
        <button
          type="button"
          data-testid={`tasks-row-${task.id}-subtask-toggle`}
          className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
          aria-expanded={expanded}
          aria-label={t('listView.subtasks')}
        >
          <Workflow className="size-3" />
          {subtaskCount}
        </button>
      )}

      {!isEditing && !disableSubtasks && depth === 0 && (
        <button
          type="button"
          data-testid={`tasks-row-${task.id}-add-subtask`}
          className="ml-auto hidden shrink-0 items-center gap-0.5 text-xs text-muted-foreground group-hover/row:flex hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            onAddSubtask();
          }}
          aria-label={t('listView.addSubtask')}
        >
          <Plus className="size-3" />
        </button>
      )}
    </div>
  );
}
